import React, { useEffect, useMemo, useRef, useState } from 'react'
import CommonInput from '../CommonInput/CommonInput.tsx'
import CommonInputType from '../../helpers/types/CommonInput.interface.ts'
import { makeStyles } from '@material-ui/core/styles'
import menuStyle from '../Menu/style.ts'
import styles from './style.ts'
import SearchableField from './SearchableField.tsx'
import SearchableMenu from './SearchableMenu'

const useMenuStyles = makeStyles(menuStyle)
const useSearchStyles = makeStyles(styles)

type HtmlInputProps = Omit<React.HTMLProps<HTMLInputElement>, 'children' | 'onChange' | 'size'>

/**
 *
 */
export type SearchableInputOptionType = {
    value?: any
    text?: React.ReactNode
    title?: boolean
    selected?: boolean
    icon?: string
    disabled?: boolean
    renderComponent?: JSX.Element
}

const defaultOptions: SearchableInputOptionType[] = [{ disabled: true, text: 'No data', value: null }]

interface PropsInterface extends HtmlInputProps, CommonInputType {
    options: SearchableInputOptionType[]
    value?: any
    onChange?(value: any): any
    onInputChange?(value: string): any
    disabled?: boolean
    fullWidth?: boolean
    placeholder?: string
    title?: string
    onOpen?: () => void
}

const normalizeOptions = (initialOptions: SearchableInputOptionType[]) => initialOptions.map((initialOption) => {
    const newOption: Record<string, any> = { ...initialOption }
    if (newOption.renderComponent) newOption.text = newOption.renderComponent
    return newOption
})

/**
 *
 */
const SearchableInput = ({
    size = 'large',
    labelPlacement,
    autoWidth,
    rules,
    isValid,
    helperText,
    options: initialOptions = defaultOptions,
    value,
    onChange,
    onInputChange,
    onClear,
    clearable = true,
    disabled = false,
    fullWidth = false,
    placeholder,
    title = 'Select an option',
    onOpen,
    className,
    'data-testid': dataTestId = 'searchable-input-field',
    ...props
}: PropsInterface): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [selectedValue, setSelectedValue] = useState(value)
    const [options, setOptions] = useState(normalizeOptions(initialOptions))
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const menuClasses = useMenuStyles({ withIcon: false })
    const searchStyles = useSearchStyles()

    const filteredOptions = useMemo(() => {
        const q = searchValue.trim().toLowerCase()
        if (!q) return options
        return options.filter((opt) => `${opt.text ?? ''}`.toLowerCase().includes(q))
    }, [options, searchValue])

    const selectedLabel = options.find((opt) => opt.value === selectedValue)?.text || ''
    const selectedLabelRef = useRef(selectedLabel)
    selectedLabelRef.current = selectedLabel

    const openMenu = () => {
        if (disabled) return
        setIsOpen(true)
        if (onOpen) onOpen()
    }

    const closeMenu = () => setIsOpen(false)

    const handleFieldClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement
        if (disabled || target.closest('.clear-input-button') || menuRef.current?.contains(target)) return
        if (target === inputRef.current) return
        if (document.activeElement === inputRef.current) openMenu()
        else inputRef.current?.focus()
    }

    const resetInput = () => {
        setInputValue('')
        setSearchValue('')
        if (onInputChange) onInputChange('')
    }

    const handleSelect = (item: SearchableInputOptionType) => {
        if (!item || item.disabled) return
        setSelectedValue(item.value)
        setInputValue(String(item.text ?? ''))
        setSearchValue('')
        setIsOpen(false)
        // Let the selected value render before the blur handler restores the label.
        window.setTimeout(() => inputRef.current?.blur(), 0)
        if (onChange && item.value !== selectedValue) onChange(item.value)
    }

    const handleInputChange = (next: string) => {
        setInputValue(next)
        setSearchValue(next)
        if (onInputChange) onInputChange(next)
        if (!isOpen) openMenu()
    }

    const handleBlur = () => {
        window.setTimeout(() => {
            if (!menuRef.current?.contains(document.activeElement)) {
                closeMenu()
                const label = selectedLabelRef.current
                if (!label) {
                    resetInput()
                } else if (!inputValue || inputValue !== String(label ?? '')) {
                    setInputValue(String(label ?? ''))
                    setSearchValue('')
                }
            }
        }, 0)
    }

    useEffect(() => {
        setOptions(normalizeOptions(initialOptions))
    }, [initialOptions])

    useEffect(() => {
        const item = options.find((opt) => opt.value === value)
        setSelectedValue(value)
        setInputValue(String(item?.text ?? ''))
        setSearchValue('')
    }, [value, options])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                closeMenu()
                const label = selectedLabelRef.current
                if (!label) resetInput()
                else {
                    setInputValue(String(label ?? ''))
                    setSearchValue('')
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [selectedValue, selectedLabel])

    return (
        <CommonInput
            value={selectedLabel || inputValue || null}
            rules={rules}
            isValid={isValid}
            helperText={helperText}
            size={size}
            fullWidth={fullWidth}
            className={`${searchStyles.commonInput} ${className || ''}`}
            label={props.label}
            labelPlacement={labelPlacement}
            autoWidth={autoWidth}
            data-testid={dataTestId}
            trailingIcon={isOpen ? 'menu-expanded' : 'menu-collapsed'}
            disabled={disabled}
            clearable={clearable}
            onClear={onClear}
            onClickWrapper={handleFieldClick}
        >
            <div className={`select ${isOpen ? 'input--focused' : ''}`} data-testid={dataTestId}>
                <SearchableField
                    inputRef={inputRef}
                    value={inputValue}
                    placeholder={placeholder || title}
                    disabled={disabled}
                    onOpen={openMenu}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    testId={`${dataTestId}-input`}
                />
                {isOpen && !disabled && (
                    <div className={menuClasses.dropdownContainer} data-testid={`${dataTestId}-menu`} ref={menuRef} style={styles.menuContainer}>
                        <div className={menuClasses.dropdown} style={styles.dropdown}>
                            <div style={styles.dropdownList}>
                                <SearchableMenu
                                    options={filteredOptions}
                                    selectedValue={selectedValue}
                                    onSelect={handleSelect}
                                    menuClasses={menuClasses}
                                    testId={dataTestId}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommonInput>
    )
}

/**
 *
 */
export type SearchableInputType = PropsInterface

export default SearchableInput
